// GuideFlowBeat.tsx — the guide as a BEAT IN THE PROGRAMME FLOW, not a Today tile.
//
// Mounted once at app level. It watches the one-shot due flags (a Key Skill chapter
// just completed → H5 mastery; the reflection-skip run just escalated → H7 over-
// pacing) and, when one is armed, shows Craig full-screen as an interstitial "step":
// a large image, the warm line, a readable quote, and a Continue. Because it rides
// the flags (set during programme activity), it appears right inside the flow — so
// it's seen while moving through lessons, where a Today-screen tile never would be.
// Suppressed (silently consumed) while paused / "my own way".
import React from 'react';
import { Btn } from '@/components/ui';
import { Icon } from '@/components/icons';
import { clipUrl, GUIDE_MESSAGES, toolUnlockedCaption } from '@/data/guideMessages';
import { TOOLS } from '@/data/toolkit';
import { useProgress } from '@/hooks/useProgress';
import { useGuide } from '@/flows/guide/useGuide';
import type { GuideSelection } from '@/services/guidePresence';

type Flag = 'mastery' | 'overpacing' | 'toolkitInvite' | 'toolkitUnlock';

export function GuideFlowBeat() {
  const progress = useProgress();
  const guide = useGuide();
  const [beat, setBeat] = React.useState<{ selection: GuideSelection; flag: Flag } | null>(null);

  // A full-screen beat must never stack on the chapter-celebration overlay (they're
  // both warm full-screen moments). While a celebration is showing, hold the beat:
  // the due-flag / toolkitAnnounced state stays armed, so it fires the moment the
  // celebration is dismissed instead — sequential, never stacked.
  const celebrating = !!progress.state.chapterCelebration;

  // H5 — key-skill mastery.
  React.useEffect(() => {
    if (beat || celebrating || !progress.state.guideMasteryDue) return;
    const selection = guide.pick('H5_mastery');
    progress.setGuideMasteryDue(false); // consume regardless (suppressed → silent)
    if (selection) { guide.record(selection); setBeat({ selection, flag: 'mastery' }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progress.state.guideMasteryDue, beat, celebrating]);

  // H7 — over-pacing.
  React.useEffect(() => {
    if (beat || celebrating || !progress.state.guideOverPacingDue) return;
    const selection = guide.pick('H7_overPacing');
    progress.setGuideOverPacingDue(false);
    if (selection) { guide.record(selection); setBeat({ selection, flag: 'overpacing' }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progress.state.guideOverPacingDue, beat, celebrating]);

  // H9 / H10 — a toolkit exercise just unlocked (its teaching lesson was completed).
  // On the FIRST unlock we show the one-time encouragement (H10) before the unlock
  // notice (H9). Each tool is announced exactly once (tracked in `toolkitAnnounced`).
  React.useEffect(() => {
    if (beat || progress.state.paused || celebrating) return;
    const done = new Set(progress.state.completedSteps);
    const tool = TOOLS.find((t) => t.unlockLessonId && done.has(t.unlockLessonId) && !progress.state.toolkitAnnounced.includes(t.id));
    if (!tool) return;
    if (!progress.state.toolkitIntroSeen) {
      progress.markToolkitIntroSeen();
      setBeat({
        selection: { hook: 'H10_toolkitInvite', clipId: GUIDE_MESSAGES.H10_toolkitInvite.clipId, caption: GUIDE_MESSAGES.H10_toolkitInvite.caption },
        flag: 'toolkitInvite',
      });
      return;
    }
    progress.markToolAnnounced(tool.id);
    setBeat({
      selection: { hook: 'H9_toolUnlocked', clipId: GUIDE_MESSAGES.H9_toolUnlocked.clipId, caption: toolUnlockedCaption(tool.title) },
      flag: 'toolkitUnlock',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progress.state.completedSteps, progress.state.toolkitAnnounced, progress.state.toolkitIntroSeen, progress.state.paused, beat, celebrating]);

  if (!beat) return null;
  const { selection } = beat;

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 93,
        background: 'var(--cream)',
        display: 'flex',
        flexDirection: 'column',
        animation: 'rise .42s cubic-bezier(.2,.7,.3,1)',
      }}
    >
      {/* soft light from above */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 320,
          background: 'radial-gradient(120% 85% at 50% -12%, rgba(214,236,226,0.7), rgba(29,158,117,0.08) 46%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          padding: 'calc(var(--safe-top) + 28px) 26px 0',
          position: 'relative',
        }}
      >
        {/* Craig — large, transparent (no box), full width shown */}
        <img
          src={clipUrl(selection.clipId)}
          alt=""
          aria-hidden="true"
          style={{ width: 188, height: 232, objectFit: 'contain', objectPosition: 'bottom center', display: 'block', marginBottom: 8 }}
        />
        <p
          style={{
            margin: 0,
            maxWidth: 380,
            fontFamily: 'Fraunces, Georgia, serif',
            fontSize: 22,
            lineHeight: 1.35,
            color: 'var(--ink)',
          }}
        >
          {selection.caption}
        </p>
        {selection.quote && (
          <div
            style={{
              maxWidth: 400,
              marginTop: 20,
              padding: '2px 0 2px 16px',
              borderLeft: '3px solid var(--green)',
              textAlign: 'left',
            }}
          >
            <p
              style={{
                margin: 0,
                fontFamily: 'Fraunces, Georgia, serif',
                fontStyle: 'italic',
                fontWeight: 400,
                fontSize: 18.5,
                lineHeight: 1.55,
                color: 'var(--green-deep)',
              }}
            >
              “{selection.quote.text}”
            </p>
          </div>
        )}
      </div>
      <div style={{ padding: '16px 26px calc(26px + var(--safe-bottom))', background: 'linear-gradient(180deg, transparent, var(--cream) 30%)' }}>
        <Btn full size="lg" onClick={() => setBeat(null)} leading={<Icon name="journey" size={18} />}>
          Continue
        </Btn>
      </div>
    </div>
  );
}
